import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { http, API_BASE } from "@/lib/api";
import {
  MapPin, Sparkle, TreeEvergreen, Drop, Mountains,
  Leaf, Plant, ArrowRight, Camera,
} from "@phosphor-icons/react";
import AdoptAPlaceModal from "@/components/AdoptAPlaceModal";

const SUBJECT_ICON = {
  tree: TreeEvergreen, forest: TreeEvergreen,
  river: Drop, stream: Drop, lake: Drop, pond: Drop, wetland: Drop,
  beach: Drop, shore: Drop, coast: Drop,
  garden: Plant, farm: Plant, field: Plant, backyard: Plant, park: Plant,
  mountain: Mountains, hill: Mountains, glacier: Mountains, meadow: Leaf,
  other: Sparkle,
};

function abs(u) { return !u ? "" : u.startsWith("http") ? u : `${API_BASE}${u}`; }

function AdoptionCard({ p }) {
  const Icon = SUBJECT_ICON[p.subject_type] || Sparkle;
  const loc = [p.location?.city, p.location?.state, p.location?.country].filter(Boolean).join(", ");
  return (
    <Link
      to={`/adopt-a-place/${p.id}`}
      data-testid={`adoption-card-${p.id}`}
      className="group block rounded-2xl overflow-hidden bg-white border border-black/10 hover:border-[color:var(--np-brand,#B85F2A)] transition-colors"
    >
      {p.cover_image ? (
        <div className="aspect-[16/10] bg-black/5 overflow-hidden">
          <img src={abs(p.cover_image)} alt={p.name} className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500" loading="lazy" />
        </div>
      ) : (
        <div className="aspect-[16/10] bg-gradient-to-br from-[#F5EBDC] to-[#E4A15C]/30 flex items-center justify-center">
          <Icon size={56} weight="fill" className="text-[color:var(--np-brand,#B85F2A)]/50" />
        </div>
      )}
      <div className="p-4">
        <div className="flex items-center gap-2 mb-1.5">
          <span className="inline-flex items-center rounded-full px-2 py-0.5 border font-mono-tech text-[9px] tracking-[0.22em] uppercase bg-[#F5EBDC] text-[#241209] border-[#E4A15C]/30">
            {p.subject_type}
          </span>
          <span className="font-mono-tech text-[9px] tracking-[0.22em] uppercase text-[#3A3830]">
            {p.schedule}
          </span>
        </div>
        <div className="font-serif-display text-lg leading-tight line-clamp-2">{p.name}</div>
        {loc && (
          <div className="mt-1 text-xs text-[#3A3830] flex items-center gap-1 line-clamp-1">
            <MapPin size={12} weight="fill" className="text-[color:var(--np-brand,#B85F2A)]" /> {loc}
          </div>
        )}
        <div className="mt-3 flex items-center justify-between font-mono-tech text-[10px] tracking-[0.24em] uppercase text-[#3A3830]">
          <span>{p.observation_count || 0} update{(p.observation_count || 0) === 1 ? "" : "s"}</span>
          <span className="inline-flex items-center gap-1 text-[color:var(--np-brand,#B85F2A)] group-hover:gap-2 transition-all">
            View timeline <ArrowRight size={11} weight="bold" />
          </span>
        </div>
      </div>
    </Link>
  );
}

export default function AdoptedPlaces() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);

  const reload = () => {
    setLoading(true);
    http.get(`/adopt-a-place`).then(({ data }) => setItems(data?.places || []))
      .catch(() => setItems([])).finally(() => setLoading(false));
  };

  useEffect(reload, []);

  return (
    <main className="bg-[color:var(--np-bg,#F5EBDC)] text-[color:var(--np-text,#151312)] min-h-screen pt-32 md:pt-36 pb-32" data-testid="adopt-a-place-page">
      <section className="max-w-[1400px] mx-auto px-5 md:px-10">
        <div className="font-mono-tech text-[10px] tracking-[0.4em] uppercase text-[color:var(--np-brand,#B85F2A)] mb-3">
          / Adopt a place — long-term nature record
        </div>
        <h1 className="font-serif-display text-5xl md:text-7xl leading-[0.92] tracking-tighter max-w-4xl">
          Same place. Every year.
        </h1>
        <p className="mt-6 font-serif-display text-base md:text-lg text-[#3A3830] max-w-3xl leading-relaxed">
          Choose a tree, a stream, a beach, a garden, a mountain — anything you can return to. Rephotograph it from
          the same viewpoint on a schedule. NaturePulse assembles your images into a long-term visual record of how a
          single place is changing.
          <br /><br />
          <span className="italic text-[color:var(--np-brand,#B85F2A)]">
            One observation is a story. Twenty years of observations from the same location becomes a dataset.
          </span>
        </p>
        <div className="mt-8 flex flex-wrap gap-3">
          <button
            onClick={() => setShowModal(true)}
            data-testid="adopt-a-place-cta"
            className="inline-flex items-center gap-2 rounded-full bg-[#B85F2A] text-white px-6 py-3 font-mono-tech text-[11px] tracking-[0.28em] uppercase hover:opacity-90"
          >
            <Sparkle size={14} weight="fill" /> Adopt a place
          </button>
          <Link
            to="/observations"
            className="inline-flex items-center gap-2 rounded-full px-6 py-3 font-mono-tech text-[11px] tracking-[0.28em] uppercase border border-black/20 hover:bg-white/60"
          >
            <Camera size={14} weight="bold" /> Back to Observations
          </Link>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-5 md:px-10 mt-14">
        <div className="flex items-baseline justify-between mb-4">
          <h2 className="font-serif-display text-2xl md:text-3xl tracking-tight">Places being watched</h2>
          <span className="font-mono-tech text-[10px] tracking-[0.24em] uppercase text-[#3A3830]/70">
            {loading ? "loading…" : `${items.length} adopted place${items.length === 1 ? "" : "s"}`}
          </span>
        </div>
        {loading ? (
          <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#3A3830]">Loading…</div>
        ) : items.length === 0 ? (
          <div className="rounded-xl border border-dashed border-black/20 p-10 text-center">
            <Sparkle size={20} weight="fill" className="mx-auto text-[color:var(--np-brand,#B85F2A)] mb-2" />
            <div className="font-serif-display text-2xl">No adoptions yet.</div>
            <div className="mt-1 text-sm text-[#3A3830]">Be the first — hit "Adopt a place" above.</div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="adoptions-grid">
            {items.map((p) => <AdoptionCard key={p.id} p={p} />)}
          </div>
        )}
      </section>

      {showModal && (
        <AdoptAPlaceModal
          onClose={() => setShowModal(false)}
          onCreated={() => { setShowModal(false); reload(); }}
        />
      )}
    </main>
  );
}
